/**
 * Cornish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Cornish (Kernewek) at a mathematician's register. **Warrant → «Gwarant»**.
 * CLDR cardinal categories are zero / one / two / few / many / other (the maximal set), so
 * `frameActive` declares all six (plus an explicit `=0`).
 *
 * NOTE (honesty over guessing): Cornish, a revived language, has the least-settled modern
 * mathematical/epistemic vocabulary of the entire programme. This catalog is a careful
 * best-effort draft and the WHOLE of it is flagged "expert-review suggested" in
 * `Agent9_REPORT.md`. Per INV-STABLE-ID any later correction changes only the string and
 * leaves every verdict fixed. W/M grades stay Latin (`::numbering-system/latn`).
 */
import { P2 } from '../message-ids-phase2.js';
export const KW_CATALOG = {
    locale: 'kw',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Hwithrans',
        [P2.sectionClaims]: 'Klemmow',
        [P2.sectionIntegrity]: 'Tiender',
        [P2.axisWarrantLabel]: 'Gwarant',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Arbrovek',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definicyon} interp {Styryans} testable {Prevadow} tested {Previs} refuted {Diskwedhys fals} program {Towl hwithrans} philos {Filosofek} other {Ankoth}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Nivel krevder {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Hwithradow',
        [P2.stateRefuted]: 'Diskwedhys fals',
        [P2.stateOutside]: "Yn-mes a'n rann",
        [P2.verdictConflict]: 'Yn kedrynna gans {constraints}',
        [P2.verdictOutside]: 'Edhom {missing}',
        [P2.lossRegisterPlain]: "Symplhes dhe yeth gomyn — rann a'n kompoester yw gesys mes.",
        [P2.lossRegisterKid]: 'Symplhes rag redyoryon yowynk — lies manylyon yw gesys mes.',
        [P2.lossProfileDetail]: 'Yma an profil dien Gwarant / Arbrovek / Modal kryno omma.',
        [P2.lossConflictDetail]: "Yma manylyon an kedrynn berrhes y'n gwel ma.",
        [P2.laneVerified]: 'Afydhys',
        [P2.laneUnverified]: 'Anafydhys',
        [P2.laneConflict]: 'Kedrynn prevys',
        [P2.constraintLogic]: 'Logyk',
        [P2.constraintLightBends]: 'An golow a wra plegya yn maes grevder',
        [P2.constraintParallelPostulate]: 'An postulat kettuel',
        [P2.claimScalarGravity]: 'Teori skalar a grevder',
        [P2.claimNonEuclidean]: 'Mynsoniethek nanEuclidek',
        [P2.claimSubstrateSr]: 'Styryans is-gwel a wrellsedhegoleth arbennek',
        [P2.edgeExtObservation]: 'Aspians a-ves',
        [P2.edgeResourceSample]: 'Asnodh jynnskrifa kevrennys',
        [P2.edgeHeuristicSample]: 'Hewisyans heuristek',
        [P2.edgeSharedSample]: 'Selven gevrennys',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Fronnow',
        [P2.sectionEdges]: 'Amalyow',
        [P2.sectionFrame]: 'Framm',
        [P2.sectionExposure]: 'Diskwedhyans',
        [P2.statusAdoptedFormal]: 'Degemmerys (furvel)',
        [P2.statusAdoptedInterpretive]: 'Degemmerys (styryansek)',
        [P2.statusSuspended]: 'Spavellys',
        [P2.edgeEffectConstraint]: 'Formya fronn (gyll diskwedhes fals)',
        [P2.edgeEffectResource]: 'Asnodh — heb effeyth war vreusow',
        [P2.edgeEffectHeuristic]: 'Heuristek — heb effeyth war vreusow',
        [P2.edgeEffectShared]: 'Selven gevrennys — heb effeyth war vreusow',
        [P2.frameActive]: '{count, plural, =0 {Fronn vewek vyth} zero {Fronn vewek vyth} one {# fronn vewek} two {# fronn vewek} few {# fronn vewek} many {# fronn vewek} other {# fronnow bewek}}',
        [P2.exposureNone]: 'Devnydhyansow styryansek vyth',
        [P2.exposureDep]: 'Yn kreghi war {id}',
        [P2.laneTrackMachine]: 'Jynn',
        [P2.laneTrackExpert]: 'Arbenniger',
        [P2.laneTrackCommunity]: 'Kemeneth',
        [P2.expertAudited]: 'Acontys',
        [P2.expertInProgress]: 'Ow mos yn-rag',
        [P2.expertNotReviewed]: 'Heb dasweles',
        [P2.communityFlags]: 'Yma baneryow',
        [P2.communityNone]: 'Baner vyth',
        [P2.controlChallenge]: 'Chalenj',
        [P2.controlActivate]: 'Bewekhe',
        [P2.controlMakeInterpretive]: 'Gul styryansek',
        [P2.controlImportEdge]: 'Ymperthi amal',
        [P2.controlPromote]: 'Ughella',
        [P2.controlUndo]: 'Diswul',
        [P2.controlRedo]: 'Dasgul',
        [P2.controlRegister]: 'Kovskrifa',
        [P2.promoteRejected]: 'Ughelheans neghys',
        [P2.promoteAccepted]: 'Ughelheans degemmerys',
        [P2.promoteReasonRefutationTerminal]: 'Ny yll prov sawya diskwedhyans fals.',
        [P2.promoteReasonKeystone]: "Ughelheans pennven a'n jevedh edhom a skoodhyans arbrovek.",
        [P2.promoteReasonEmpiricalNotFixed]: 'Nyns yw an eghen arbrovek fastys.',
        [P2.promoteReasonModalNotFixed]: 'Nyns yw an gradh modal fastys.',
        [P2.promoteReasonWarrantNotImproved]: 'Nyns yw gwellhes an gradh gwarant.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=kw.js.map